import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { ChatMessage, StepConfig } from '@/types'
import { STEPS } from '@/config/steps'

function generateId(): string {
  return Math.random().toString(36).substring(2, 9)
}

export const useChatStore = defineStore('chat', () => {
  // State
  const messages = ref<ChatMessage[]>([])
  const currentStepIndex = ref(0)
  const isTyping = ref(false)
  const isSubmitting = ref(false)

  // Computed
  const currentStep = computed<StepConfig | null>(() => {
    return STEPS[currentStepIndex.value] || null
  })

  const progressPercent = computed(() => {
    return Math.round(((currentStepIndex.value) / STEPS.length) * 100)
  })

  const isComplete = computed(() => {
    return currentStepIndex.value >= STEPS.length
  })

  // Actions
  function addMessage(message: Omit<ChatMessage, 'id' | 'timestamp'>) {
    messages.value.push({
      ...message,
      id: generateId(),
      timestamp: Date.now(),
    })
    saveProgress()
  }

  function addBotMessage(content: string, stepId?: string) {
    addMessage({
      type: 'bot',
      content,
      stepId,
    })
  }

  function addUserMessage(content: string, value?: any, stepId?: string) {
    addMessage({
      type: 'user',
      content,
      value,
      stepId,
    })
  }

  function setTyping(value: boolean) {
    isTyping.value = value
  }

  function setSubmitting(value: boolean) {
    isSubmitting.value = value
  }

  function advanceStep() {
    if (currentStepIndex.value < STEPS.length) {
      currentStepIndex.value++
      saveProgress()
    }
  }

  function goBack() {
    if (currentStepIndex.value > 0) {
      // Remove messages from current step
      const currentStepId = currentStep.value?.id
      if (currentStepId) {
        messages.value = messages.value.filter(m => m.stepId !== currentStepId)
      }

      // Go back one step
      currentStepIndex.value--

      // Remove the user's answer from the previous step (so they can re-answer)
      // But keep the bot message
      const prevStepId = currentStep.value?.id
      if (prevStepId) {
        messages.value = messages.value.filter(m => !(m.stepId === prevStepId && m.type === 'user'))
      }

      saveProgress()
    }
  }

  function saveProgress() {
    localStorage.setItem('chat_step', String(currentStepIndex.value))
    localStorage.setItem('chat_messages', JSON.stringify(messages.value))
  }

  function restoreProgress() {
    const savedStep = localStorage.getItem('chat_step')
    const savedMessages = localStorage.getItem('chat_messages')

    if (savedStep) {
      const step = parseInt(savedStep, 10)
      // Allow step to be STEPS.length (complete state)
      if (!isNaN(step) && step >= 0 && step <= STEPS.length) {
        currentStepIndex.value = step
      }
    }

    if (savedMessages) {
      try {
        messages.value = JSON.parse(savedMessages)
      } catch {
        messages.value = []
      }
    }
  }

  function reset() {
    messages.value = []
    currentStepIndex.value = 0
    isTyping.value = false
    isSubmitting.value = false
    localStorage.removeItem('chat_step')
    localStorage.removeItem('chat_messages')
  }

  return {
    // State
    messages,
    currentStepIndex,
    isTyping,
    isSubmitting,
    // Computed
    currentStep,
    progressPercent,
    isComplete,
    // Actions
    addMessage,
    addBotMessage,
    addUserMessage,
    setTyping,
    setSubmitting,
    advanceStep,
    goBack,
    restoreProgress,
    reset,
  }
})
